import {
    PREVIEW_ROOMS,
    previewNights,
    quotePreviewNight,
    type PreviewRoomKey,
} from "./pricePreviewModel";

/**
 * The numbers behind the welcome page's feature sketches. Like the price
 * preview they are worked examples rather than a property's data, and they
 * are the same example: the sketches quote the price the preview explains.
 */

/** The booking poller's tick: the shortest interval at which a property is checked. */
export const SYNC_TICK_MINUTES = 30;

/**
 * The check that last ran and the one that runs next, on the half-hour grid.
 * Both the sync chip and the monitor read it, so the two sketches on one page
 * never state two different last checks.
 */
export function syncTicks(now: Date): { last: Date; next: Date } {
    const last = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours(),
        now.getMinutes() - (now.getMinutes() % SYNC_TICK_MINUTES),
    );
    return { last, next: new Date(last.getTime() + SYNC_TICK_MINUTES * 60_000) };
}

/** The price the hero's preview opens on: the family room's Saturday. */
export function exampleNightPrice(today: Date): number {
    const family = PREVIEW_ROOMS.find((room) => room.key === "family") ?? PREVIEW_ROOMS[0];
    const nights = previewNights(today);
    const saturday = nights.find((night) => night.date.getDay() === 6) ?? nights[0];
    return quotePreviewNight(family, saturday).price;
}

/**
 * What the example night's competitors charge, as the median the product
 * reads: below the property's own price, which is why the preview's Saturday
 * says the competitors are cheaper. The scale's ends frame the two marks.
 */
export const EXAMPLE_MARKET = { median: 3600, scaleMin: 3000, scaleMax: 4200 } as const;

/** Where a price sits on the market scale, in percent of its width. */
export function marketScalePosition(price: number): number {
    const { scaleMin, scaleMax } = EXAMPLE_MARKET;
    const share = (price - scaleMin) / (scaleMax - scaleMin);
    return Math.min(Math.max(share, 0), 1) * 100;
}

/** The linked rate plans' adjustments on the example night, in percent of the base plan. */
export const EXAMPLE_RATE_PLANS = { channelPct: 8, nonRefundablePct: -10, roundingStep: 10 } as const;

/** A linked plan's price: the base plan's moved by its adjustment, on the property's rounding step. */
export function linkedPlanPrice(basePrice: number, adjustmentPct: number): number {
    const step = EXAMPLE_RATE_PLANS.roundingStep;
    return Math.round((basePrice * (1 + adjustmentPct / 100)) / step) * step;
}

/**
 * The example weekend's minimum stay per room type, in nights: Friday,
 * Saturday, Sunday. Sunday is down to one night everywhere, the family room
 * holds three over the Saturday.
 */
export const EXAMPLE_MIN_STAY: Record<PreviewRoomKey, readonly [number, number, number]> = {
    double: [2, 2, 1],
    family: [2, 3, 1],
    suite: [2, 2, 1],
};

export interface FixedHoliday {
    /** 1–12. */
    month: number;
    day: number;
    /** ISO 3166-1 alpha-2 codes of the countries where the day is a public holiday nationwide. */
    countries: readonly string[];
}

/**
 * Public holidays that fall on the same date every year and are nationwide in
 * the countries named — the property's own country and the markets its guests
 * come from, which is what the product's holiday list is built from. Regional
 * holidays and the ones that move with Easter are left out: the sketch states
 * a date, and a date is only safe to state where it holds for a whole country
 * every year.
 */
const FIXED_HOLIDAYS: readonly FixedHoliday[] = [
    { month: 1, day: 6, countries: ["AT", "PL"] },
    { month: 5, day: 3, countries: ["PL"] },
    { month: 5, day: 8, countries: ["CZ"] },
    { month: 7, day: 5, countries: ["CZ"] },
    { month: 8, day: 15, countries: ["AT", "PL"] },
    { month: 9, day: 28, countries: ["CZ"] },
    { month: 10, day: 3, countries: ["DE"] },
    { month: 10, day: 26, countries: ["AT"] },
    { month: 10, day: 28, countries: ["CZ"] },
    { month: 11, day: 11, countries: ["PL"] },
    { month: 11, day: 17, countries: ["CZ"] },
    { month: 12, day: 8, countries: ["AT"] },
];

export interface UpcomingHoliday {
    date: Date;
    countries: readonly string[];
}

/** The next `count` holidays from today on, today's included, soonest first. */
export function nextHolidays(today: Date, count: number): UpcomingHoliday[] {
    const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return FIXED_HOLIDAYS.map(({ month, day, countries }) => {
        const thisYear = new Date(midnight.getFullYear(), month - 1, day);
        const date = thisYear < midnight ? new Date(midnight.getFullYear() + 1, month - 1, day) : thisYear;
        return { date, countries };
    })
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .slice(0, count);
}
